import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Clock, ArrowRight, BookOpen } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * TopReadsWidget
 * --------------
 * Self-tuning blog discovery widget — pulls the top cornerstone posts from
 * /api/blog/top-reads (which itself aggregates `cornerstone_click` analytics
 * events from the last 7 days). Whichever cornerstone post is converting
 * best gets the "🔥 Most-read this week" badge automatically — no manual
 * curation.
 *
 * Privacy-safe: the API only returns slugs + titles + a coarse popularity
 * badge. No raw click counts ever leave the server.
 */
const TopReadsWidget = () => {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/blog/top-reads?days=7&limit=3`)
      .then((res) => { if (!cancelled) setPosts(res.data?.posts || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!posts || posts.length === 0) return null;

  return (
    <section
      data-testid="top-reads-widget"
      className="py-16 px-4 border-t border-white/5"
    >
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-2 mb-2">
          <BookOpen className="w-4 h-4 text-amber-400" />
          <p className="text-[11px] font-bold tracking-widest text-amber-400">
            TOP READS THIS WEEK
          </p>
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2 tracking-tight">
          What FinOps leaders are reading
        </h2>
        <p className="text-sm text-slate-400 mb-8 max-w-2xl">
          Auto-curated from real reader engagement — updated every week, no editorial bias.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {posts.map((post, i) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              data-testid={`top-reads-card-${post.slug}`}
              className="group relative rounded-xl bg-slate-900 border border-white/10 hover:border-amber-400/40 p-5 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-amber-500/5"
            >
              <div className="absolute -top-2 left-4">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold tracking-wide ${
                  i === 0
                    ? 'bg-amber-500 text-slate-950'
                    : i === 1
                      ? 'bg-cyan-500/90 text-slate-950'
                      : 'bg-slate-700 text-slate-200'
                }`}>
                  {post.badge}
                </span>
              </div>
              <div className="text-[11px] font-semibold tracking-widest text-slate-400 uppercase mb-2 mt-2">
                {post.category}
              </div>
              <h3 className="text-base font-bold text-white mb-3 leading-tight group-hover:text-amber-200 transition-colors line-clamp-3">
                {post.title}
              </h3>
              <div className="flex items-center justify-between text-[11px] text-slate-400 pt-3 border-t border-white/5">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {post.read_time}
                </span>
                <span className="flex items-center gap-1 text-amber-400 font-semibold group-hover:gap-2 transition-all">
                  Read
                  <ArrowRight className="w-3 h-3" />
                </span>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-6 text-center">
          <Link
            to="/blog"
            data-testid="top-reads-see-all-link"
            className="text-sm text-slate-400 hover:text-amber-300 transition-colors inline-flex items-center gap-1"
          >
            Browse all FinOps deep dives
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default TopReadsWidget;
